import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  fetchUserReports, 
  fetchMonthlyScores,
  submitProgressReport,
  clearErrors,
  clearSuccess
} from '../../store/slices/performanceReviewSlice';
import { startOfWeek, endOfWeek, isAfter } from 'date-fns';
import './index.scss';
import ReportCalendar from './components/ReportCalendar';
import ReportForm from './components/ReportForm';
import MonthlyScores from './components/MonthlyScores';

const PerformanceReview = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { 
    userReports, 
    monthlyScores, 
    loading, 
    error, 
    submitLoading, 
    submitError, 
    submitSuccess 
  } = useSelector((state) => state.performanceReview);
  
  // Set default week to current week (starting Monday)
  const [selectedRange, setSelectedRange] = useState({
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
  });
  
  const [currentReport, setCurrentReport] = useState(null);
  const [, setReportStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('submit');
  
  // Fetch user reports when component mounts
  useEffect(() => {
    if (user?.email) {
      dispatch(fetchUserReports(user.email));
      dispatch(fetchMonthlyScores(user.email));
    }
  }, [dispatch, user?.email]);
  
  // Update selected report when reports are fetched or selectedRange changes
  useEffect(() => {
    if (userReports && userReports.length > 0) {
      // Find report for selected week
      const report = userReports.find(report => 
        report.weekStartDate.getTime() === selectedRange.startDate.getTime()
      );
      
      if (report) {
        setCurrentReport(report);
        setReportStatus(report.status);
      } else {
        setCurrentReport(null);
        setReportStatus(null);
      }
    }
  }, [userReports, selectedRange]);
  
  // Reset success message after 3 seconds
  useEffect(() => {
    if (submitSuccess) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitSuccess, dispatch]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      dispatch(clearErrors());
      dispatch(clearSuccess());
    };
  }, [dispatch]);
  
  const handleRangeChange = (range) => {
    // Don't allow selecting future weeks
    const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    if (isAfter(range.startDate, currentWeekStart)) {
      return;
    }
    
    setSelectedRange(range);
    
    // Find report for the selected week
    const report = userReports.find(report => 
      report.weekStartDate.getTime() === range.startDate.getTime()
    );
    
    if (report) {
      setCurrentReport(report);
      setReportStatus(report.status);
    } else {
      setCurrentReport(null);
      setReportStatus(null);
    }
  };
  
  const isWeekEditable = (weekStart) => {
    // Don't allow future weeks
    const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    if (isAfter(weekStart, currentWeekStart)) {
      return false;
    }
    
    // Find existing report for this week
    const existingReport = userReports.find(
      report => report.weekStartDate.getTime() === weekStart.getTime()
    );
    
    // Allow editing if:
    // 1. No report exists (can submit new report)
    // 2. Report exists and status is "pending" (can edit)
    // 3. Report exists and status is "blank" (can edit)
    return !existingReport || 
           existingReport.status === 'pending' || 
           existingReport.status === 'blank';
  };
  
  const handleReportSubmit = async (formData) => {
    // Don't allow future dates
    if (isAfter(selectedRange.startDate, startOfWeek(new Date(), { weekStartsOn: 1 }))) {
      return;
    }
    
    const reportData = {
      email: user.email,
      weekStartDate: selectedRange.startDate,
      weekEndDate: selectedRange.endDate,
      ...formData
    };
    
    if (currentReport?.id) {
      // Update existing report
      await dispatch(submitProgressReport({ 
        reportData, 
        isUpdate: true, 
        reportId: currentReport.id 
      }));
    } else {
      // Create new report
      await dispatch(submitProgressReport({ reportData }));
    }
    
    // Refresh reports after submission
    if (!submitError) {
      dispatch(fetchUserReports(user.email));
    }
  };
  
  const getSubmittedWeeks = () => {
    return userReports.map(report => ({
      startDate: report.weekStartDate,
      endDate: report.weekEndDate,
      status: report.status
    }));
  };
  
  return (
    <div className="performance-review-page">
      <div className="page-header">
        <h1>Weekly Progress Report</h1>
      </div>
      
      <div className="performance-review-content">
        {error && (
          <div className="alert error">
            <p>{error}</p>
            <button className="close-btn" onClick={() => dispatch(clearErrors())}>×</button>
          </div>
        )}
        
        {submitError && (
          <div className="alert error">
            <p>{submitError}</p>
            <button className="close-btn" onClick={() => dispatch(clearErrors())}>×</button>
          </div>
        )}
        
        {submitSuccess && (
          <div className="alert success">
            <p>Report submitted successfully!</p>
            <button className="close-btn" onClick={() => dispatch(clearSuccess())}>×</button>
          </div>
        )}
        
        <div className="tabs-container">
          <div className="tabs-header">
            <div 
              className={`tab ${activeTab === 'submit' ? 'active' : ''}`}
              onClick={() => setActiveTab('submit')}
            >
              Submit Report
            </div>
            <div 
              className={`tab ${activeTab === 'scores' ? 'active' : ''}`}
              onClick={() => setActiveTab('scores')}
            >
              Score Overview
            </div>
          </div>
          
          <div className="tab-content">
            {activeTab === 'submit' && (
              <div className="submit-tab">
                <ReportCalendar
                  selectedRange={selectedRange}
                  onRangeChange={handleRangeChange}
                  submittedWeeks={getSubmittedWeeks()}
                />
                
                <ReportForm
                  currentReport={currentReport}
                  onSubmit={handleReportSubmit}
                  isLoading={submitLoading}
                  isReadOnly={!isWeekEditable(selectedRange.startDate)}
                />
              </div>
            )}
            
            {activeTab === 'scores' && (
              <MonthlyScores
                scores={monthlyScores}
                isLoading={loading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceReview; 